import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, X, Package2, Box, ChevronDown, ChevronRight, FileText, Trash2 } from "lucide-react";
import { api, formatApiError } from "@/api/client";

const FIELD = "w-full border border-slate-300 rounded-sm px-2 py-1.5 text-sm";
const inr = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

const EMPTY_LINE = { item_name: "", quantity: "", unit: "", unit_price: "", hsn: "", request_id: null };

/** Store Module Phase B — "Buy Stock Items".
 * A project-less PO form that goes into central inventory once received.
 * `seedLines` (iter 84) lets the Purchase Board pre-fill this from a
 * batch of stock purchase requests — each seeded line carries its
 * `request_id` so the backend can auto-fulfill the request on PO create.
 */
export function StockPOPanel({ vendors, refreshVendors, onCreated, seedLines }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    vendor_id: "", delivery_date: "", notes: "",
    lines: [{ ...EMPTY_LINE }], extras: [],
  });
  const [saving, setSaving] = useState(false);
  const [showVendorForm, setShowVendorForm] = useState(false);
  const [vendorDraft, setVendorDraft] = useState({ name: "", gst: "", phone: "", email: "", address: "" });
  const [savingVendor, setSavingVendor] = useState(false);

  // If parent hands us pre-filled lines (from a request), swap them in and open.
  useEffect(() => {
    if (seedLines && seedLines.length > 0) {
      setForm((F) => ({
        ...F,
        lines: seedLines.map((s) => ({
          item_name: s.item_name || "",
          quantity: s.quantity || "",
          unit: s.unit || "",
          unit_price: s.unit_price || "",
          hsn: s.hsn || "",
          request_id: s.request_id || null,
        })),
      }));
      setOpen(true);
    }
  }, [seedLines]);

  const setLine = (i, f, v) => setForm((F) => ({ ...F, lines: F.lines.map((ln, idx) => idx === i ? { ...ln, [f]: v } : ln) }));
  const addLine = () => setForm((F) => ({ ...F, lines: [...F.lines, { ...EMPTY_LINE }] }));
  const rmLine = (i) => setForm((F) => ({ ...F, lines: F.lines.length > 1 ? F.lines.filter((_, idx) => idx !== i) : [{ ...EMPTY_LINE }] }));
  const setExtra = (i, f, v) => setForm((F) => ({ ...F, extras: F.extras.map((e, idx) => idx === i ? { ...e, [f]: v } : e) }));
  const addExtra = (preset) => setForm((F) => ({ ...F, extras: [...(F.extras || []), preset || { label: "", amount: "" }] }));
  const rmExtra = (i) => setForm((F) => ({ ...F, extras: F.extras.filter((_, idx) => idx !== i) }));

  const submit = async () => {
    if (!form.vendor_id) { toast.error("Pick a vendor"); return; }
    const cleaned = form.lines
      .filter((ln) => ln.item_name.trim() && Number(ln.quantity) > 0 && Number(ln.unit_price) >= 0)
      .map((ln) => ({
        item_name: ln.item_name.trim(),
        quantity: Number(ln.quantity),
        unit: ln.unit || null,
        unit_price: Number(ln.unit_price),
        hsn: ln.hsn || null,
        request_id: ln.request_id || null,
      }));
    if (cleaned.length === 0) { toast.error("Add at least one valid line"); return; }
    const extrasClean = (form.extras || [])
      .map((x) => ({ label: (x.label || "").trim(), amount: Number(x.amount) || 0 }))
      .filter((x) => x.label);
    setSaving(true);
    try {
      const { data } = await api.post("/costing/purchase-orders/stock", {
        vendor_id: form.vendor_id,
        delivery_date: form.delivery_date || null,
        notes: form.notes || null,
        lines: cleaned, extras: extrasClean,
      });
      toast.success(`Stock PO ${data.po_number} created`);
      setForm({ vendor_id: "", delivery_date: "", notes: "", lines: [{ ...EMPTY_LINE }], extras: [] });
      setOpen(false);
      onCreated && onCreated();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };
  const submitVendor = async () => {
    const name = (vendorDraft.name || "").trim();
    if (!name) { toast.error("Vendor name is required"); return; }
    setSavingVendor(true);
    try {
      const { data } = await api.post("/costing/vendors", { name, ...vendorDraft });
      toast.success(`Vendor "${name}" created`);
      setVendorDraft({ name: "", gst: "", phone: "", email: "", address: "" });
      setShowVendorForm(false);
      await refreshVendors();
      setForm((f) => ({ ...f, vendor_id: data.id }));
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSavingVendor(false); }
  };
  const lineTotal = (ln) => (Number(ln.quantity) || 0) * (Number(ln.unit_price) || 0);
  const subtotal = form.lines.reduce((s, ln) => s + lineTotal(ln), 0);
  const extrasTotal = (form.extras || []).reduce((s, x) => s + (Number(x.amount) || 0), 0);

  return (
    <div className="bg-gradient-to-r from-amber-50 to-white border border-amber-200 rounded-sm"
      data-testid="stock-po-panel">
      <button type="button" onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-3 py-2.5 hover:bg-amber-50/60 text-left"
        data-testid="stock-po-toggle">
        <div className="flex items-center gap-2">
          <Package2 className="w-4 h-4 text-amber-700" />
          <div>
            <div className="hk-tiny-label text-amber-800">Buy Stock Items</div>
            <div className="text-[11px] text-slate-600">
              Raise a PO with no project link — items will land in central inventory once received.
            </div>
          </div>
        </div>
        {open ? <ChevronDown className="w-4 h-4 text-amber-700" /> : <ChevronRight className="w-4 h-4 text-amber-700" />}
      </button>
      {open && (
        <div className="p-3 border-t border-amber-200 bg-white space-y-2">
          <div className="flex items-center gap-1">
            <select className={FIELD + " flex-1"} value={form.vendor_id}
              onChange={(e) => setForm({ ...form, vendor_id: e.target.value })}
              data-testid="stock-po-vendor">
              <option value="">Select vendor…</option>
              {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
            </select>
            <button type="button" onClick={() => setShowVendorForm((v) => !v)}
              className="text-[10px] font-bold uppercase text-amber-700 hover:text-amber-900 shrink-0 px-2">
              {showVendorForm ? <X className="w-3 h-3" /> : <Plus className="w-3 h-3" />}
            </button>
          </div>
          {showVendorForm && (
            <div className="border border-amber-200 bg-amber-50 rounded-sm p-2 space-y-1">
              <input className={FIELD} placeholder="Vendor name (required)"
                value={vendorDraft.name}
                onChange={(e) => setVendorDraft({ ...vendorDraft, name: e.target.value })} />
              <div className="grid grid-cols-2 gap-1">
                <input className={FIELD} placeholder="GST" value={vendorDraft.gst}
                  onChange={(e) => setVendorDraft({ ...vendorDraft, gst: e.target.value })} />
                <input className={FIELD} placeholder="Phone" value={vendorDraft.phone}
                  onChange={(e) => setVendorDraft({ ...vendorDraft, phone: e.target.value })} />
              </div>
              <button type="button" onClick={submitVendor} disabled={savingVendor || !vendorDraft.name.trim()}
                className="w-full bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-white font-bold px-2 py-1.5 rounded-sm text-[11px] uppercase">
                {savingVendor ? <Loader2 className="w-3 h-3 animate-spin inline mr-1" /> : null} Save vendor
              </button>
            </div>
          )}
          <input className={FIELD} type="date" placeholder="Delivery date"
            value={form.delivery_date}
            onChange={(e) => setForm({ ...form, delivery_date: e.target.value })} />
          <div className="space-y-1" data-testid="stock-po-lines">
            {form.lines.map((ln, i) => (
              <div key={i} className="border border-slate-100 rounded-sm p-1 space-y-1 bg-white">
                <input className={FIELD} placeholder="Item name" value={ln.item_name}
                  onChange={(e) => setLine(i, "item_name", e.target.value)}
                  data-testid={`stock-po-line-name-${i}`} />
                <div className="grid grid-cols-4 gap-1">
                  <input className={FIELD} type="number" placeholder="Qty" value={ln.quantity}
                    onChange={(e) => setLine(i, "quantity", e.target.value)} />
                  <input className={FIELD} placeholder="Unit" value={ln.unit}
                    onChange={(e) => setLine(i, "unit", e.target.value)} />
                  <input className={FIELD} type="number" step="0.01" placeholder="Rate" value={ln.unit_price}
                    onChange={(e) => setLine(i, "unit_price", e.target.value)} />
                  <input className={FIELD} placeholder="HSN" value={ln.hsn}
                    onChange={(e) => setLine(i, "hsn", e.target.value)} />
                </div>
                <div className="flex items-center justify-between text-[10px] text-slate-500">
                  <span>Line total: <b>{inr(lineTotal(ln))}</b></span>
                  <button type="button" onClick={() => rmLine(i)}
                    className="text-red-600 hover:text-red-800">Remove</button>
                </div>
              </div>
            ))}
          </div>
          <button type="button" onClick={addLine}
            className="text-[10px] font-bold uppercase text-amber-700 hover:text-amber-900">
            + Add line
          </button>
          <div className="flex items-center justify-between pt-1 border-t border-slate-200">
            <div className="text-[10px] uppercase font-bold text-slate-500">Charges & taxes</div>
            <div className="flex items-center gap-1">
              <button type="button" onClick={() => addExtra({ label: "Tax", amount: "" })}
                className="text-[10px] font-bold uppercase text-amber-700 hover:text-amber-900">+ Tax</button>
              <button type="button" onClick={() => addExtra({ label: "Transport", amount: "" })}
                className="text-[10px] font-bold uppercase text-amber-700 hover:text-amber-900">+ Transport</button>
              <button type="button" onClick={() => addExtra()}
                className="text-[10px] font-bold uppercase text-amber-700 hover:text-amber-900">+ Other</button>
            </div>
          </div>
          {(form.extras || []).map((ex, i) => (
            <div key={i} className="flex items-center gap-1">
              <input className={FIELD + " flex-1"} placeholder="Label" value={ex.label}
                onChange={(e) => setExtra(i, "label", e.target.value)} />
              <input className={FIELD + " w-28"} type="number" step="0.01" placeholder="Amount"
                value={ex.amount}
                onChange={(e) => setExtra(i, "amount", e.target.value)} />
              <button type="button" onClick={() => rmExtra(i)}
                className="text-red-600 hover:text-red-800 shrink-0 px-1">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
          <textarea className={FIELD} rows={2} placeholder="Notes (optional)"
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          <div className="text-[11px] text-right text-slate-600">Subtotal: {inr(subtotal)}</div>
          {(form.extras || []).length > 0 && (
            <div className="text-[11px] text-right text-slate-600">Extras: {inr(extrasTotal)}</div>
          )}
          <div className="text-xs font-bold text-right text-slate-900">Total: {inr(subtotal + extrasTotal)}</div>
          <button type="button" onClick={submit} disabled={saving || !form.vendor_id}
            className="w-full bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-white font-bold px-3 py-2 rounded-sm text-xs"
            data-testid="stock-po-submit">
            {saving ? <Loader2 className="w-3 h-3 animate-spin inline mr-1" /> : null} Create Stock PO
          </button>
        </div>
      )}
    </div>
  );
}

/** Store Module Phase C rewire (iter 84) — Stock Requests panel on the
 * Purchase Board. Admin/Accounts pick which pending requests they'll
 * convert into a single stock PO; the panel then pushes them as
 * `seedLines` down to StockPOPanel with the `request_id` attached.
 */
export function StockRequestsPanel({ onSelect }) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [checked, setChecked] = useState({});   // {req_id: true}

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/costing/stock-requests?status=open");
      setRows(data.requests || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (open) load(); }, [open]);

  const selectedCount = Object.values(checked).filter(Boolean).length;

  const proceed = () => {
    const picked = rows.filter((r) => checked[r.id]);
    if (picked.length === 0) { toast.error("Pick at least one request"); return; }
    const seeds = picked.map((r) => ({
      item_name: r.item_name,
      quantity: r.quantity,
      unit: r.unit || "",
      unit_price: "",   // vendor-negotiated, still to be entered
      request_id: r.id,
    }));
    onSelect(seeds, `${picked.length} request${picked.length === 1 ? "" : "s"}`);
    // Uncheck + reload so the fulfilled ones will disappear after save.
    setChecked({});
    // Small optimistic close of the panel — parent's StockPOPanel opens automatically.
    setOpen(false);
  };

  return (
    <div className="bg-gradient-to-r from-sky-50 to-white border border-sky-200 rounded-sm"
      data-testid="stock-requests-panel">
      <button type="button" onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-3 py-2.5 hover:bg-sky-50/60 text-left"
        data-testid="stock-requests-toggle">
        <div className="flex items-center gap-2">
          <FileText className="w-4 h-4 text-sky-700" />
          <div>
            <div className="hk-tiny-label text-sky-800">Store Requests</div>
            <div className="text-[11px] text-slate-600">
              Purchase requests raised by the Store. Tick them and turn into a stock PO in one shot.
            </div>
          </div>
        </div>
        {open ? <ChevronDown className="w-4 h-4 text-sky-700" /> : <ChevronRight className="w-4 h-4 text-sky-700" />}
      </button>
      {open && (
        <div className="p-3 border-t border-sky-200 space-y-2">
          {loading ? (
            <div className="p-4 text-center text-slate-400 text-xs">Loading…</div>
          ) : rows.length === 0 ? (
            <div className="p-4 text-center text-slate-400 text-xs" data-testid="stock-requests-empty">
              No open requests from the Store.
            </div>
          ) : (
            <>
              <ul className="divide-y divide-slate-100 max-h-60 overflow-y-auto border border-slate-200 rounded-sm">
                {rows.map((r) => (
                  <li key={r.id} className="flex items-center gap-2 p-2 hover:bg-slate-50 text-[11px]"
                    data-testid={`stock-request-row-${r.id}`}>
                    <input type="checkbox" checked={!!checked[r.id]}
                      onChange={(e) => setChecked({ ...checked, [r.id]: e.target.checked })}
                      data-testid={`stock-request-check-${r.id}`} />
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-slate-800 truncate">{r.item_name}</div>
                      <div className="text-[10px] text-slate-500">
                        {r.quantity}{r.unit ? ` ${r.unit}` : ""} · from {r.requested_by_name}
                        {r.notes ? ` · ${r.notes}` : ""}
                      </div>
                    </div>
                    <span className="text-[9px] font-mono text-slate-400">{(r.requested_at || "").slice(0, 10)}</span>
                  </li>
                ))}
              </ul>
              <button type="button" onClick={proceed} disabled={selectedCount === 0}
                className="w-full bg-sky-600 hover:bg-sky-700 disabled:opacity-50 text-white font-bold px-3 py-2 rounded-sm text-xs uppercase inline-flex items-center gap-1 justify-center"
                data-testid="stock-requests-create-po">
                <Plus className="w-3 h-3" /> Create Stock PO from {selectedCount || "…"} request{selectedCount === 1 ? "" : "s"}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}


/** Store Module Phase B — Consume from stock on a project row.
 * Reads /costing/inventory once per open, shows a compact list with an
 * "Issue" button per item that pops a qty prompt and hits the issue API.
 */
export function ConsumeFromStockPanel({ project, onIssued }) {
  const [open, setOpen] = useState(false);
  const [inv, setInv] = useState([]);
  const [loading, setLoading] = useState(false);
  const [issuing, setIssuing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/costing/inventory");
      setInv(data.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (open) load(); }, [open]);

  const issue = async (item) => {
    const raw = window.prompt(`Issue how many ${item.unit || "units"} of "${item.name}" to ${project.project_pid}?\n\nOn hand: ${item.on_hand_qty}`, "");
    if (raw === null) return;
    const qty = Number(raw);
    if (!qty || qty <= 0) { toast.error("Enter a positive quantity"); return; }
    if (qty > item.on_hand_qty) { toast.error(`Only ${item.on_hand_qty} in stock`); return; }
    setIssuing(item.id);
    try {
      await api.post(`/costing/inventory/${item.id}/issue`, {
        project_id: project.project_id, qty,
      });
      toast.success(`Issued ${qty} ${item.unit || ""} of ${item.name}`);
      await load();
      onIssued && onIssued();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setIssuing(null); }
  };

  return (
    <div className="bg-white border border-emerald-200 rounded-sm">
      <button type="button" onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-2 py-1.5 text-[11px] hover:bg-emerald-50 text-left"
        data-testid={`consume-stock-toggle-${project.project_id}`}>
        <div className="flex items-center gap-1.5">
          <Box className="w-3.5 h-3.5 text-emerald-700" />
          <span className="hk-tiny-label text-emerald-800">Consume from Stock</span>
        </div>
        {open ? <ChevronDown className="w-3 h-3 text-emerald-700" /> : <ChevronRight className="w-3 h-3 text-emerald-700" />}
      </button>
      {open && (
        <div className="border-t border-emerald-100 max-h-56 overflow-y-auto"
          data-testid={`consume-stock-list-${project.project_id}`}>
          {loading ? (
            <div className="p-3 text-center text-[11px] text-slate-400">Loading inventory…</div>
          ) : inv.length === 0 ? (
            <div className="p-3 text-center text-[11px] text-slate-400">Nothing in stock yet. Create a Stock PO above.</div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {inv.map((item) => (
                <li key={item.id} className="flex items-center justify-between gap-2 p-1.5 hover:bg-slate-50 text-[11px]">
                  <div className="min-w-0">
                    <div className="font-semibold text-slate-800 truncate">{item.name}</div>
                    <div className="text-[10px] text-slate-500">
                      On hand: <b>{item.on_hand_qty}</b>{item.unit ? ` ${item.unit}` : ""}
                      {" · "}avg {inr(item.avg_unit_price)}
                    </div>
                  </div>
                  <button type="button" onClick={() => issue(item)} disabled={issuing === item.id}
                    className="text-[10px] font-bold uppercase text-emerald-700 hover:text-emerald-900 disabled:opacity-50 shrink-0"
                    data-testid={`consume-stock-issue-${item.id}`}>
                    {issuing === item.id ? "…" : "Issue"}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
